import { FC, useEffect, useState } from "react";
import { List } from "@refinedev/antd";
import { useList, useUpdate } from "@refinedev/core";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Spin,
  Typography,
} from "antd";
import dayjs from "dayjs";

import {
  TvLeaguePage,
  tvErrorNotification,
  tvSuccessNotification,
} from "../Lib";
import { TvImageField, TvVideoField } from "../Components";

const LeagueForm: FC<{ page: TvLeaguePage; onSaved: () => void }> = ({
  page,
  onSaved,
}) => {
  const [form] = Form.useForm<TvLeaguePage>();
  const { mutate, isLoading } = useUpdate();
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    form.setFieldsValue(page);
    setDirty(false);
  }, [page, form]);

  const save = (values: TvLeaguePage) =>
    mutate(
      {
        resource: "tv-leagues",
        dataProviderName: "tvDataProvider",
        id: page.leagueId,
        values,
        ...tvSuccessNotification(`Шапка «${page.leagueTitle}» сохранена`),
        ...tvErrorNotification(),
      },
      { onSuccess: onSaved }
    );

  return (
    <Card
      title={page.leagueTitle}
      extra={
        <Typography.Text type="secondary">
          {page.updatedAt
            ? `изменено ${dayjs(page.updatedAt).format("DD.MM.YYYY HH:mm")}`
            : "ещё не заполнялась"}
        </Typography.Text>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={save}
        onValuesChange={() => setDirty(true)}
      >
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="logoUrl" label="Логотип лиги (хедер ТВ)">
              <TvImageField label="Логотип" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="bannerUrl"
              label="Фото шапки"
              extra="Без видео — PNG или WebP с прозрачным фоном: игроки без фона, обрезаны по низу. Высота 1030 px, ширина до 1000 px — на сайте фото высотой 514 px, двойной размер нужен для чётких экранов. Встаёт справа, низом к низу шапки, видно только на компьютере. С видео — первый кадр этого видео, JPG или WebP 1920×640: стоит на всю шапку, пока видео грузится и если оно не запустилось, потом видео плавно проявляется поверх."
            >
              <TvImageField label="Фото" />
            </Form.Item>
            <Form.Item name="videoUrl" label="Видео шапки">
              <TvVideoField hint="1920×640 (3:1), 10–20 секунд, mp4 или webm до 25 МБ (лучше до 10 МБ — быстрее грузится на телефоне). Крутится без звука по кругу на всю шапку. По краям срезается до 10%, на телефоне видна только середина — главное держите в центре кадра. В «Фото шапки» загрузите первый кадр этого видео — тогда переход незаметен." />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item
          name="description"
          label="Описание соревнования"
          rules={[{ max: 5000 }]}
        >
          <Input.TextArea autoSize={{ minRows: 3, maxRows: 8 }} />
        </Form.Item>
        <Typography.Title level={5}>Партнёр лиги</Typography.Title>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="partnerTitle"
              label="Название"
              rules={[{ max: 255 }]}
            >
              <Input placeholder="PARI" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="partnerUrl"
              label="Ссылка"
              rules={[
                {
                  pattern: /^https?:\/\//,
                  message: "Ссылка должна начинаться с http:// или https://",
                },
              ]}
            >
              <Input placeholder="https://…" />
            </Form.Item>
          </Col>
        </Row>
        <Form.Item name="partnerLogoUrl" label="Логотип партнёра">
          <TvImageField label="Логотип партнёра" />
        </Form.Item>
        <Space>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            disabled={!dirty}
          >
            Сохранить
          </Button>
          <Typography.Text type="secondary">
            Пустое поле на сайте не показывается
          </Typography.Text>
        </Space>
      </Form>
    </Card>
  );
};

export const TvLeaguesPage = () => {
  const { data, isLoading, refetch } = useList<TvLeaguePage>({
    resource: "tv-leagues",
    dataProviderName: "tvDataProvider",
    pagination: { mode: "off" },
  });

  return (
    <List
      title="ФНЛ ТВ — шапки лиг"
      resource="tv-leagues"
      breadcrumb={false}
      canCreate={false}
    >
      <Typography.Paragraph type="secondary">
        Страница лиги на ФНЛ ТВ: фото или видео и описание соревнования, логотип
        в хедере, кнопка партнёра. Сезон, тур, число клубов и прогресс турнира
        берутся из БД ФНЛ сами.
      </Typography.Paragraph>
      <Spin spinning={isLoading}>
        <Space direction="vertical" size={24} style={{ width: "100%" }}>
          {(data?.data ?? []).map((page) => (
            <LeagueForm
              key={page.leagueId}
              page={page}
              onSaved={() => refetch()}
            />
          ))}
        </Space>
      </Spin>
    </List>
  );
};
